/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Shared state and refs for the ViewAppointments page hook.
 * @module pages/viewAppointments/useViewAppointmentsContext
 */

import { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { DEFAULT_RESULTS_LIMIT } from "./viewAppointmentsConstants.js";

/** Locale-aware collator for sorting company names in buyer pickers. */
export const companyNameCollator = new Intl.Collator("es", {
  sensitivity: "base",
  numeric: true,
});

/**
 * Holds ViewAppointments page state, router helpers, and dialog refs.
 * @returns {Object}
 */
const useViewAppointmentsContext = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("below");
  const [requirements, setRequirements] = useState([]);
  const [userData, setUserData] = useState(null);
  const [participatingCompanyData, setParticipatingCompanyData] =
    useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buyerCompanies, setBuyerCompanies] = useState([]);
  const [selectedBuyerCompany, setSelectedBuyerCompany] = useState(null);
  const [buyerExecutives, setBuyerExecutives] = useState([]);
  const [selectedBuyerExecutive, setSelectedBuyerExecutive] = useState(null);
  const [participatingCompanies, setParticipatingCompanies] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [showRescheduleSuccessMessage, setShowRescheduleSuccessMessage] =
    useState(false);
  const [isRescheduleSlidingUp, setIsRescheduleSlidingUp] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [senderAppointments, setSenderAppointments] = useState([]);
  const [allAppointmentsForConflict, setAllAppointmentsForConflict] = useState(
    [],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const appointmentsPageRequestRef = useRef(0);

  const [showVirtualMeetingModal, setShowVirtualMeetingModal] = useState(false);
  const [
    showVirtualAppointmentErrorModal,
    setShowVirtualAppointmentErrorModal,
  ] = useState(false);
  const [selectedAppointmentForVirtual, setSelectedAppointmentForVirtual] =
    useState(null);
  const [isConvertingToVirtual, setIsConvertingToVirtual] = useState(false);
  const [virtualAppointmentError, setVirtualAppointmentError] = useState("");

  const confirmationDialogRef = useRef(null);
  const virtualAppointmentDialogRef = useRef(null);
  const virtualAppointmentErrorDialogRef = useRef(null);
  const companyRequestIdRef = useRef(0);

  const [isFiltersDropdownOpen, setIsFiltersDropdownOpen] = useState(false);
  const [filtersDropdownPosition, setFiltersDropdownPosition] = useState({
    top: 0,
    left: 0,
  });
  const [filtersDropdownDirection, setFiltersDropdownDirection] =
    useState("below");

  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [selectedRequirementsFilter, setSelectedRequirementsFilter] =
    useState(null);

  return {
    t,
    navigate,
    location,
    searchQuery,
    setSearchQuery,
    selectedRequirement,
    setSelectedRequirement,
    appointments,
    setAppointments,
    eventData,
    setEventData,
    filteredRequests,
    setFilteredRequests,
    dropdownPosition,
    setDropdownPosition,
    activeDropdownId,
    setActiveDropdownId,
    dropdownDirection,
    setDropdownDirection,
    requirements,
    setRequirements,
    userData,
    setUserData,
    participatingCompanyData,
    setParticipatingCompanyData,
    isLoading,
    setIsLoading,
    buyerCompanies,
    setBuyerCompanies,
    selectedBuyerCompany,
    setSelectedBuyerCompany,
    buyerExecutives,
    setBuyerExecutives,
    selectedBuyerExecutive,
    setSelectedBuyerExecutive,
    participatingCompanies,
    setParticipatingCompanies,
    showSuccessMessage,
    setShowSuccessMessage,
    isSlidingUp,
    setIsSlidingUp,
    showRescheduleSuccessMessage,
    setShowRescheduleSuccessMessage,
    isRescheduleSlidingUp,
    setIsRescheduleSlidingUp,
    showConfirmModal,
    setShowConfirmModal,
    senderAppointments,
    setSenderAppointments,
    allAppointmentsForConflict,
    setAllAppointmentsForConflict,
    currentPage,
    setCurrentPage,
    resultsLimit,
    setResultsLimit,
    totalAppointments,
    setTotalAppointments,
    debouncedSearch,
    setDebouncedSearch,
    appointmentsPageRequestRef,
    showVirtualMeetingModal,
    setShowVirtualMeetingModal,
    showVirtualAppointmentErrorModal,
    setShowVirtualAppointmentErrorModal,
    selectedAppointmentForVirtual,
    setSelectedAppointmentForVirtual,
    isConvertingToVirtual,
    setIsConvertingToVirtual,
    virtualAppointmentError,
    setVirtualAppointmentError,
    confirmationDialogRef,
    virtualAppointmentDialogRef,
    virtualAppointmentErrorDialogRef,
    companyRequestIdRef,
    isFiltersDropdownOpen,
    setIsFiltersDropdownOpen,
    filtersDropdownPosition,
    setFiltersDropdownPosition,
    filtersDropdownDirection,
    setFiltersDropdownDirection,
    selectedStatusFilter,
    setSelectedStatusFilter,
    selectedRequirementsFilter,
    setSelectedRequirementsFilter,
  };
};

export default useViewAppointmentsContext;
